import {
  Anchor,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  Stack,
  Switch,
  Text,
  ThemeIcon,
} from "@mantine/core"
import { getModuleUrl, useTenant } from "@dehub/theme"
import {
  ArrowCounterClockwiseIcon,
  ArrowSquareOutIcon,
  CurrencyDollarIcon,
  RobotIcon,
  ScalesIcon,
  StorefrontIcon,
} from "@phosphor-icons/react"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { OrderApi } from "../../apis"
import * as ConsignmentOfferingApi from "../../apis/consignment-offering"
import { usePermission } from "../permission"
import {
  IOrderPlus,
} from "../../models/Order"
import { IAuth } from "../../store/Auth"
import { message } from "../../utils/message"
import { renderBusinessName } from "../../utils/render-business-name"
import { OrderTypeBadge } from "../active-deliveries/atoms"

interface CommissionRow {
  label: string
  percent: number
}

interface CommissionSummary {
  defaultPercent?: number
  perCategory?: CommissionRow[]
  /** Average projected profit % for this order (lines × commission). */
  averageProfitPercent?: number
  /** Min profit threshold from offering policy — colors avg green if ≥ threshold. */
  thresholdPercent?: number
}

interface Props {
  order: IOrderPlus
  /** Caller passes a refetch handler so the panel can reload after revert. */
  onAfterRevert?: () => void
  /** R16 §4.2 — commission policy + projected profit summary. */
  commissionSummary?: CommissionSummary | null
  /** R16 §4.6 — current persisted value for the VMI auto-approve flag. */
  vmiAutoApprove?: boolean
  /** Called after successful toggle so parent can refetch. */
  onAfterVmiAutoApprove?: (next: boolean) => void
}

/**
 * R16 — Consignment context panel for Order detail page (supplier view).
 *
 * Mirrors the DeliveryNote panel (R8) so a supplier reviewing an order
 * has immediate context about the offering, contract, and source. Renders
 * only when orderType is consignment-related.
 *
 * Inventory snapshot ("Тус retailer-ийн одоогийн нөөц") is split into a
 * follow-up slice (Phase G18f) — it needs a new aggregator endpoint that
 * pulls ConsignmentInventory for the receiver retailer.
 */
export function OrderConsignmentContextPanel({
  order,
  onAfterRevert,
  commissionSummary,
  vmiAutoApprove,
  onAfterVmiAutoApprove,
}: Props) {
  const { t } = useTranslation("supplierSales")
  const tenant = useTenant()
  const { accessToken } = useSelector((s: { auth: IAuth }) => s.auth)
  const ORD_VMI_REVERT = usePermission("ORD_VMI_REVERT")
  const [reverting, setReverting] = useState(false)
  const [autoApprove, setAutoApprove] = useState<boolean>(vmiAutoApprove ?? false)
  const [autoApproveBusy, setAutoApproveBusy] = useState(false)
  const orderType = order.orderType ?? "REGULAR"

  if (orderType === "REGULAR") return null

  const canRevert =
    order.source === "VMI_CRON" &&
    order.orderStatus === "AUTHORIZED" &&
    ORD_VMI_REVERT.hasAccess

  const handleVmiAutoApproveToggle = async (next: boolean) => {
    if (!order.receiverBusinessId) return
    setAutoApproveBusy(true)
    setAutoApprove(next)
    try {
      const expiresAt = next
        ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        : null
      await ConsignmentOfferingApi.setVmiAutoApprove({
        retailerBusinessId: order.receiverBusinessId,
        enabled           : next,
        expiresAt,
      })
      message.success(
        next
          ? t("consignCtx.autoApproveOn")
          : "VMI auto-approve unchecked",
      )
      onAfterVmiAutoApprove?.(next)
    } catch (err) {
      // revert local state on failure
      setAutoApprove(!next)
      message.error((err as Error).message ?? t("consignCtx.autoApproveConfigFail"))
    } finally {
      setAutoApproveBusy(false)
    }
  }

  const handleRevert = async () => {
    if (!canRevert || !order.id) return
    const reason = window.prompt(
      t("consignCtx.revertPrompt"),
    )
    setReverting(true)
    try {
      await OrderApi.vmiRevert(order.id, reason ?? null)
      message.success(t("consignCtx.revertSuccess"))
      onAfterRevert?.()
    } catch (err) {
      message.error((err as Error).message ?? t("consignCtx.revertFail"))
    } finally {
      setReverting(false)
    }
  }

  const contractUrl = getModuleUrl("contract", tenant?.modulesDomain)
  const consignmentUrl = getModuleUrl("consignment", tenant?.modulesDomain)
  const tokenSuffix = accessToken ? `?token=${accessToken}` : ""

  const agreementId = order.consignmentAgreementId
  const retailerBusinessId = order.receiverBusinessId
  const retailerName = renderBusinessName(order.receiverBusiness, "") || null

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      mb="md"
      style={{
        borderColor: "var(--mantine-color-violet-3)",
        background: "var(--mantine-color-violet-0)",
      }}
    >
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap={10} align="center">
            <ThemeIcon size={30} radius={10} variant="light" color="violet">
              <ScalesIcon size={16} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text fz={14} fw={700} tt="uppercase" c="violet.7" style={{ letterSpacing: "0.08em" }}>
                {t("consignCtx.title")}
              </Text>
              <Text fz={14} c="dimmed">
                {t(`consignCtx.typeTitle.${orderType}`, { defaultValue: "" })}
              </Text>
            </Stack>
          </Group>

          {canRevert && (
            <Button
              size="sm"
              variant="light"
              color="orange"
              leftSection={<ArrowCounterClockwiseIcon size={14} />}
              onClick={handleRevert}
              loading={reverting}
            >
              {t("consignCtx.rejectRevert")}
            </Button>
          )}
        </Group>

        <Group gap="md" wrap="wrap">
          <Box>
            <Text fz={14} fw={600} tt="uppercase" c="dimmed">
              {t("consignCtx.type")}
            </Text>
            <OrderTypeBadge
              orderType={orderType}
              source={order.source ?? null}
              size="md"
            />
          </Box>
          {order.source && (
            <Box>
              <Text fz={14} fw={600} tt="uppercase" c="dimmed">
                Source
              </Text>
              <Text fz={14} fw={500}>
                {t(`consignCtx.sourceDesc.${order.source}`, { defaultValue: order.source })}
              </Text>
            </Box>
          )}
          {agreementId && (
            <Box>
              <Text fz={14} fw={600} tt="uppercase" c="dimmed">
                {t("consignCtx.contract")}
              </Text>
              <Anchor
                href={`${contractUrl}/contracts/${agreementId}${tokenSuffix}`}
                size="sm"
                fw={500}
              >
                <Group gap={4} wrap="nowrap">
                  <Text component="span" ff="monospace" fz={14}>
                    {agreementId.slice(0, 8)}…
                  </Text>
                  <ArrowSquareOutIcon size={12} />
                </Group>
              </Anchor>
            </Box>
          )}
          {retailerBusinessId && (
            <Box>
              <Text fz={14} fw={600} tt="uppercase" c="dimmed">
                Retailer
              </Text>
              <Anchor
                href={`${consignmentUrl}/consignment/retailers/${retailerBusinessId}${tokenSuffix}`}
                size="sm"
                fw={500}
              >
                <Group gap={4} wrap="nowrap">
                  <StorefrontIcon size={12} weight="duotone" />
                  {retailerName ?? retailerBusinessId.slice(0, 8)}
                  <ArrowSquareOutIcon size={11} />
                </Group>
              </Anchor>
            </Box>
          )}
        </Group>

        {commissionSummary && (
          <>
            <Divider variant="dashed" my={4} />
            <Stack gap="xs">
              <Group gap={6}>
                <ThemeIcon size={22} radius="md" variant="light" color="teal">
                  <CurrencyDollarIcon size={12} />
                </ThemeIcon>
                <Text fz={14} fw={700} tt="uppercase" c="teal.7" style={{ letterSpacing: "0.06em" }}>
                  {t("consignCtx.commissionProfit")}
                </Text>
              </Group>
              <Group gap="lg" wrap="wrap">
                {commissionSummary.defaultPercent !== undefined && (
                  <Box>
                    <Text fz={14} fw={600} tt="uppercase" c="dimmed">
                      Default commission
                    </Text>
                    <Text fz={14} fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                      {commissionSummary.defaultPercent.toFixed(1)}%
                    </Text>
                  </Box>
                )}
                {commissionSummary.perCategory?.slice(0, 4).map((row) => (
                  <Box key={row.label}>
                    <Text fz={14} fw={600} tt="uppercase" c="dimmed">
                      {row.label}
                    </Text>
                    <Text fz={14} fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                      {row.percent.toFixed(1)}%
                    </Text>
                  </Box>
                ))}
                {commissionSummary.averageProfitPercent !== undefined && (
                  <Box>
                    <Text fz={14} fw={600} tt="uppercase" c="dimmed">
                      {t("consignCtx.avgProfit")}
                    </Text>
                    {(() => {
                      const avg = commissionSummary.averageProfitPercent ?? 0
                      const threshold = commissionSummary.thresholdPercent ?? 15
                      const aboveThreshold = avg >= threshold
                      return (
                        <Group gap={6} wrap="nowrap" align="center">
                          <Text
                            fz={14}
                            fw={700}
                            c={aboveThreshold ? "teal" : "red"}
                            style={{ fontVariantNumeric: "tabular-nums" }}
                          >
                            {avg.toFixed(1)}%
                          </Text>
                          <Badge
                            size="xs"
                            variant="light"
                            color={aboveThreshold ? "teal" : "red"}
                          >
                            {aboveThreshold
                              ? `≥ ${threshold}%`
                              : `< ${threshold}% threshold`}
                          </Badge>
                        </Group>
                      )
                    })()}
                  </Box>
                )}
              </Group>
            </Stack>
          </>
        )}

        {/* R16 §4.6 — VMI auto-approve toggle. Only shown for replenishment
            consignment orders, since Initial Fill / Return / Transfer don't
            participate in cron-driven replenishment. */}
        {orderType === "CONSIGNMENT_REPLENISHMENT" && order.receiverBusinessId && (
          <>
            <Divider variant="dashed" my={4} />
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <ThemeIcon size={28} radius="md" variant="light" color="indigo">
                <RobotIcon size={16} />
              </ThemeIcon>
              <Stack gap={4} style={{ flex: 1 }}>
                <Group gap="xs" justify="space-between" wrap="nowrap">
                  <Text size="sm" fw={500}>
                    {t("consignCtx.vmiAutoApprove")}
                  </Text>
                  <Switch
                    checked={autoApprove}
                    onChange={(e) =>
                      handleVmiAutoApproveToggle(e.currentTarget.checked)
                    }
                    disabled={autoApproveBusy}
                    size="sm"
                    color="indigo"
                  />
                </Group>
                <Text size="sm" c="dimmed">
                  {t("consignCtx.vmiAutoApproveHint")}
                </Text>
              </Stack>
            </Group>
          </>
        )}

        {/* Inventory snapshot is a follow-up slice (Phase G18f) — needs an
            aggregator endpoint that returns ConsignmentInventory for the
            receiver retailer. Keep a placeholder hint so reviewers know where
            the post-approval projection will appear. */}
        <Box
          p="xs"
          style={{
            background: "var(--mantine-color-gray-0)",
            borderRadius: 6,
            border: "1px dashed var(--mantine-color-gray-3)",
          }}
        >
          <Group gap="xs">
            <Badge variant="dot" color="gray" size="xs">
              Phase G18f
            </Badge>
            <Text size="sm" c="dimmed">
              {t("consignCtx.inventoryHint")}
            </Text>
          </Group>
        </Box>
      </Stack>
    </Paper>
  )
}
